import React from 'react'
import './styles/notification-delivery-builder.scss'

import { Well } from '@pushly/aqe/lib/components'
import { InfoCircleOutlined } from '@ant-design/icons'
import { NotificationExperienceModel } from '../../../models/notification/notification-experience.model'
import { calculateValueFromRawTtlSeconds } from '../../data-form-elements'
import { calculateRawSecondsFromValue, calculateValueFromRawSeconds } from '../../simple-timeframe/simple-timeframe'
import { getClassNames } from '../../../_utils/classnames'
import { DomainDto } from '../../../dtos/domain'
import { getVariantTtlConfig } from '../helpers'
import { FEAT_NOTIF_REQUIRE_INTERACTION_TOGGLE, FEAT_SILENT_DELIVERY_NOTIF } from '../../../constants'
import { hasFlag } from '../../../core/flags/has-flag'
import {
    COLLAPSE_KEY_REPLACE_ALL,
    COLLAPSE_KEY_STACK,
    NotificationCollapseBehavior,
    resolveBehaviorFromCollapseKey,
} from '@pushly/aqe/lib/utils/notification-collapse-key'
import { Flex, Form, Input, Popover, Radio, Segmented, Select, Skeleton, Space, Switch, Typography } from 'antd'

interface INotificationDeliveryBuilder {
    className?: string
    domain: DomainDto
    disabled?: boolean
    value?: NotificationExperienceModel | any
    onChange?: (value: NotificationExperienceModel) => any
    loading?: boolean
    title?: string
    hideHeading?: boolean
    hideTtlSettings?: boolean
    hideCollapseKeySettings?: boolean
}

const collapseBehaviorPopoverContent = (
    <div className="collapse-key-behavior-popover-content">
        <p>
            <strong>Stack</strong>
            Shows each notification.
        </p>
        <p>
            <strong>Replace All</strong>
            Closes all currently shown notifications before displaying this one.
        </p>
        <p>
            <strong>Replacement Group</strong>
            Enter a group key to have only notifications with the same key replace each other. Use letters, numbers,
            dashes, and underscores only.
        </p>
    </div>
)

const NotificationExperienceOptions = (props: INotificationDeliveryBuilder) => {
    const {
        className,
        domain,
        loading,
        disabled,
        value,
        onChange,
        title,
        hideHeading,
        hideTtlSettings,
        hideCollapseKeySettings,
    } = props
    const [tmpValue, setTmpValue] = React.useState<NotificationExperienceModel>(
        NotificationExperienceModel.build(value ?? {}),
    )
    const [replacementGroupDraft, setReplacementGroupDraft] = React.useState<string>('')

    const workingModel = (!!value ? value : tmpValue).clone()

    const domainHasRIFlag = domain.flags?.includes(FEAT_NOTIF_REQUIRE_INTERACTION_TOGGLE)
    const ttlConfig = getVariantTtlConfig(domain, workingModel)
    const calculatedTtl = calculateValueFromRawSeconds(ttlConfig.seconds, ttlConfig.metric)

    const hasIsSilentFlag = hasFlag(domain, FEAT_SILENT_DELIVERY_NOTIF)
    const collapseBehaviorData = resolveBehaviorFromCollapseKey(workingModel.getCollapseKey(), domain.collapseKeyType)
    const collapseBehavior = collapseBehaviorData.behavior

    React.useEffect(() => {
        if (collapseBehavior === NotificationCollapseBehavior.REPLACEMENT_GROUP) {
            setReplacementGroupDraft(collapseBehaviorData.replacementGroupKey ?? '')
        }
    }, [collapseBehavior, collapseBehaviorData.replacementGroupKey])

    return (
        <Well title={title ?? 'Delivery'} mode="ghost" hideHeader={hideHeading} footerMode="hidden">
            <Skeleton loading={loading} active={true} avatar={false} title={false}>
                <Flex vertical gap={6}>
                    {hasIsSilentFlag && (
                        <label>
                            <Switch
                                size="small"
                                checked={workingModel.getIsSilent()}
                                onChange={(isSilent) => {
                                    const update = workingModel.clone()
                                    update.setIsSilent(isSilent)

                                    setTmpValue(update)
                                    onChange?.(update)
                                }}
                                aria-label="Silent Delivery"
                            />
                            <span className="label-with-component-l-r">Silent Delivery</span>
                            <Popover
                                classNames={{
                                    content: getClassNames('notification-builder-popover', 'is-silent-popover'),
                                }}
                                content={
                                    <Typography.Paragraph>
                                        When enabled, this notification will be delivered with no alert sound or
                                        vibration on supported mobile devices.
                                    </Typography.Paragraph>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </label>
                    )}

                    {domainHasRIFlag && (
                        <div>
                            <label>
                                <Switch
                                    size="small"
                                    disabled={disabled}
                                    checked={workingModel.getRequireInteraction()}
                                    onChange={(requireInteraction) => {
                                        const update = workingModel.clone()
                                        update.setRequireInteraction(requireInteraction)

                                        setTmpValue(update)
                                        onChange?.(update)
                                    }}
                                    aria-label="Require Interaction"
                                />
                                <span className="label-with-component-l-r">Require Interaction</span>
                            </label>
                            <Popover
                                classNames={{
                                    content: getClassNames(
                                        'notification-builder-popover',
                                        'require-interaction-popover',
                                    ),
                                }}
                                content={
                                    <Typography.Paragraph>
                                        When disabled, this notification will minimize to the notification center after
                                        approximately 8 seconds. Chrome for Android is not affected as all notifications
                                        are minimized to the notification tray regardless of this setting.
                                    </Typography.Paragraph>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </div>
                    )}

                    {!hideCollapseKeySettings && (
                        <Flex align="start" gap={12}>
                            <Form.Item
                                className="display-replacement-behavior"
                                label={
                                    <span>
                                        Display / Replacement Behavior
                                        <Popover
                                            classNames={{
                                                content: getClassNames(
                                                    'notification-builder-popover',
                                                    'collapse-key-behavior-popover',
                                                ),
                                            }}
                                            content={collapseBehaviorPopoverContent}
                                        >
                                            <InfoCircleOutlined className="info-icon" />
                                        </Popover>
                                    </span>
                                }
                                layout="vertical"
                            >
                                <Radio.Group
                                    size="small"
                                    buttonStyle="solid"
                                    disabled={disabled}
                                    value={collapseBehavior}
                                    onChange={(ev) => {
                                        const nextBehavior = ev.target.value as NotificationCollapseBehavior
                                        const update = workingModel.clone()

                                        if (nextBehavior === NotificationCollapseBehavior.STACK) {
                                            update.setCollapseKey(COLLAPSE_KEY_STACK)
                                        } else if (nextBehavior === NotificationCollapseBehavior.REPLACE_ALL) {
                                            update.setCollapseKey(COLLAPSE_KEY_REPLACE_ALL)
                                        } else {
                                            const nextKey = replacementGroupDraft
                                            update.setCollapseKey(nextKey)
                                        }

                                        setTmpValue(update)
                                        onChange?.(update)
                                    }}
                                    options={[
                                        { label: 'Stack', value: NotificationCollapseBehavior.STACK },
                                        { label: 'Replace All', value: NotificationCollapseBehavior.REPLACE_ALL },
                                        {
                                            label: 'Replacement Group',
                                            value: NotificationCollapseBehavior.REPLACEMENT_GROUP,
                                        },
                                    ]}
                                    optionType="button"
                                />
                            </Form.Item>
                            {collapseBehavior === NotificationCollapseBehavior.REPLACEMENT_GROUP && (
                                <Form.Item label="Group Key" layout="vertical" style={{ flex: 1 }}>
                                    <Input
                                        aria-label="Group Key"
                                        autoFocus={true}
                                        size="small"
                                        placeholder="e.g., sale-banner, sports-score, todays-forecast"
                                        disabled={disabled}
                                        value={replacementGroupDraft}
                                        onChange={(ev) => {
                                            const nextKey = ev.target.value
                                            const update = workingModel.clone()
                                            setReplacementGroupDraft(nextKey)
                                            update.setCollapseKey(nextKey)

                                            setTmpValue(update)
                                            onChange?.(update)
                                        }}
                                    />
                                </Form.Item>
                            )}
                        </Flex>
                    )}

                    {!hideTtlSettings && (
                        <Form.Item
                            className="lifespan"
                            layout="vertical"
                            label={
                                <label className="lifespan-label">
                                    <span className="label-with-component-r">Lifespan</span>
                                    <Popover
                                        classNames={{
                                            content: getClassNames('notification-builder-popover', 'ttl-popover'),
                                        }}
                                        content={
                                            <div>
                                                <Typography.Paragraph>
                                                    Notification lifespan determines how long the notification will
                                                    attempt to be delivered to the subscriber.
                                                </Typography.Paragraph>
                                                <Typography.Paragraph>
                                                    <Typography.Text strong>Example:</Typography.Text> A notification
                                                    with a lifespan of 1 day will not be delivered to a user who does
                                                    not become eligible for delivery within 24 hours of the notification
                                                    being sent.
                                                </Typography.Paragraph>
                                            </div>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                </label>
                            }
                        >
                            <Space.Compact>
                                <Input
                                    style={{ width: 75 }}
                                    placeholder="0"
                                    disabled={disabled}
                                    value={calculatedTtl}
                                    onChange={(ev) => {
                                        let v: any = ev.target.value
                                        const update = workingModel.clone()

                                        try {
                                            v = calculateRawSecondsFromValue(v, workingModel.getTtlMetric())
                                        } catch (_) {}
                                        update.setTtlSeconds(v)

                                        setTmpValue(update)
                                        onChange?.(update)
                                    }}
                                />
                                <Select
                                    style={{ width: 105 }}
                                    classNames={{
                                        popup: { root: 'lifespan-metric-overlay' },
                                    }}
                                    disabled={disabled}
                                    value={workingModel.getTtlMetric() ?? 'days'}
                                    onChange={(metric) => {
                                        const update = workingModel.clone()
                                        const previousTtl = calculateValueFromRawTtlSeconds(
                                            workingModel.getTtlSeconds(),
                                            workingModel.getTtlMetric(),
                                        )

                                        update.setTtlMetric(metric)
                                        try {
                                            update.setTtlSeconds(calculateRawSecondsFromValue(previousTtl, metric))
                                        } catch (_) {}

                                        setTmpValue(update)
                                        onChange?.(update)
                                    }}
                                    options={[
                                        { value: 'minutes', label: 'Minute(s)' },
                                        { value: 'hours', label: 'Hour(s)' },
                                        { value: 'days', label: 'Day(s)' },
                                    ]}
                                />
                            </Space.Compact>
                        </Form.Item>
                    )}
                </Flex>
            </Skeleton>
        </Well>
    )
}

export default NotificationExperienceOptions
